import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Chip,
  MenuItem,
} from "@mui/material";

import {
  getAdminCouriers,
  setUserSuspended,
  updateCourierVerification,
} from "../../api/adminApi";

const displayName = (row) => {
  const name = [row.first_name, row.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    name ||
    row.courier_name ||
    row.username ||
    "-"
  );
};

const displayLocation = (row) => {
  const location = [
    row.city,
    row.state,
    row.country,
  ]
    .filter(Boolean)
    .join(", ");

  return location || "-";
};

const displayVehicle = (row) => {
  const vehicle = [
    row.year,
    row.make,
    row.model,
    row.vehicle_type,
    row.truck_body_type,
  ]
    .filter(Boolean)
    .join(" ");

  return vehicle || "Not provided";
};

const isFullyVerified = (row) =>
  !!row.license_verified &&
  !!row.insurance_verified &&
  !!row.vehicle_verified;

export default function AdminCouriers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingCourierId, setSavingCourierId] =
    useState(null);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] =
    useState("ALL");
  const [
    verificationFilter,
    setVerificationFilter,
  ] = useState("ALL");

  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [reason, setReason] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await getAdminCouriers();
      setItems(res.data?.items || []);
    } catch (e) {
      console.error(e);

      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        `Failed to load drivers/carriers (HTTP ${
          e?.response?.status || "?"
        })`;

      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const visibleItems = useMemo(() => {
    const q = String(search || "")
      .trim()
      .toLowerCase();

    return items.filter((row) => {
      if (
        statusFilter === "ACTIVE" &&
        row.suspended
      ) {
        return false;
      }

      if (
        statusFilter === "SUSPENDED" &&
        !row.suspended
      ) {
        return false;
      }

      const fullyVerified =
        isFullyVerified(row);

      if (
        verificationFilter === "VERIFIED" &&
        !fullyVerified
      ) {
        return false;
      }

      if (
        verificationFilter === "NEEDS_REVIEW" &&
        fullyVerified
      ) {
        return false;
      }

      if (!q) {
        return true;
      }

      const searchable = [
        row.courier_id,
        row.user_id,
        row.first_name,
        row.last_name,
        row.courier_name,
        row.email,
        row.phone,
        row.username,
        row.courier_type,
        row.vehicle_type,
        row.truck_body_type,
        row.make,
        row.model,
        row.year,
        row.city,
        row.state,
        row.country,
      ]
        .filter(
          (value) =>
            value !== null &&
            value !== undefined
        )
        .join(" ")
        .toLowerCase();

      return searchable.includes(q);
    });
  }, [
    items,
    search,
    statusFilter,
    verificationFilter,
  ]);

  const openSuspendDialog = (row) => {
    setTarget(row);
    setReason(
      row?.suspended_reason || ""
    );
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
    setTarget(null);
    setReason("");
  };

  const confirmSuspendToggle = async () => {
    if (!target) {
      return;
    }

    setError("");

    try {
      const nextSuspended =
        !target.suspended;

      if (
        nextSuspended &&
        !String(reason).trim()
      ) {
        setError(
          "Please enter a reason for suspension."
        );
        return;
      }

      await setUserSuspended(
        target.user_id,
        nextSuspended,
        String(reason || "").trim()
      );

      closeDialog();
      await load();
    } catch (e) {
      console.error(e);

      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        `Failed to update user status (HTTP ${
          e?.response?.status || "?"
        })`;

      setError(msg);
    }
  };

  const setFlag = (
    courierId,
    field,
    value
  ) => {
    setItems((prev) =>
      prev.map((courier) =>
        courier.courier_id ===
        courierId
          ? {
              ...courier,
              [field]: value,
            }
          : courier
      )
    );
  };

  const saveVerification = async (
    courierId
  ) => {
    setError("");
    setSavingCourierId(courierId);

    try {
      const courier = items.find(
        (item) =>
          item.courier_id ===
          courierId
      );

      if (!courier) {
        return;
      }

      await updateCourierVerification(
        courierId,
        {
          license_verified:
            !!courier.license_verified,
          insurance_verified:
            !!courier.insurance_verified,
          vehicle_verified:
            !!courier.vehicle_verified,
        }
      );

      await load();
    } catch (e) {
      console.error(e);

      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        `Failed to update verification (HTTP ${
          e?.response?.status || "?"
        })`;

      setError(msg);
    } finally {
      setSavingCourierId(null);
    }
  };

  return (
    <Box>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          mb: 1,
        }}
      >
        Drivers / Carriers
      </Typography>

      <Typography
        sx={{
          mb: 2,
          color: "text.secondary",
        }}
      >
        Driver and carrier identity,
        equipment, verification and
        account controls.
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: {
            xs: "stretch",
            md: "center",
          },
          flexDirection: {
            xs: "column",
            md: "row",
          },
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <TextField
          label="Search drivers / carriers"
          placeholder="Name, email, phone, vehicle..."
          size="small"
          value={search}
          onChange={(e) =>
            setSearch(e.target.value)
          }
          sx={{
            minWidth: {
              md: 300,
            },
          }}
        />

        <TextField
          select
          label="Status"
          size="small"
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(
              e.target.value
            )
          }
          sx={{
            minWidth: {
              md: 150,
            },
          }}
        >
          <MenuItem value="ALL">
            All
          </MenuItem>

          <MenuItem value="ACTIVE">
            Active
          </MenuItem>

          <MenuItem value="SUSPENDED">
            Suspended
          </MenuItem>
        </TextField>

        <TextField
          select
          label="Verification"
          size="small"
          value={verificationFilter}
          onChange={(e) =>
            setVerificationFilter(
              e.target.value
            )
          }
          sx={{
            minWidth: {
              md: 180,
            },
          }}
        >
          <MenuItem value="ALL">
            All
          </MenuItem>

          <MenuItem value="VERIFIED">
            Fully Verified
          </MenuItem>

          <MenuItem value="NEEDS_REVIEW">
            Needs Review
          </MenuItem>
        </TextField>

        <Button
          variant="outlined"
          onClick={load}
          disabled={loading}
        >
          Refresh
        </Button>

        <Typography
          sx={{
            color: "text.secondary",
          }}
        >
          {visibleItems.length} shown
        </Typography>
      </Box>

      {loading && (
        <Typography
          sx={{
            mb: 2,
            color: "text.secondary",
          }}
        >
          Loading…
        </Typography>
      )}

      {error && (
        <Typography
          sx={{
            mb: 2,
            color: "error.main",
          }}
        >
          {error}
        </Typography>
      )}

      <Typography
        variant="caption"
        sx={{
          display: { xs: "block", md: "none" },
          mb: 1,
          color: "text.secondary",
        }}
      >
        Swipe table left/right to view all details and actions.
      </Typography>

     <TableContainer
        component={Paper}
        sx={{
          width: "100%",
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          maxWidth: "100%",
          borderRadius: 2,
          "&::-webkit-scrollbar": {
            height: 8,
           },
         }}
      >
        <Table
          size="small"
          sx={{
            minWidth: 1250,
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  position: "sticky",
                  left: 0,
                  zIndex: 3,
                  backgroundColor: "#f8fafc",
                  minWidth: 170,
                  borderRight: "1px solid #e5e7eb",
                }}
              >
                Name
              </TableCell>

              <TableCell>
                Contact
              </TableCell>

              <TableCell>
                Type / Vehicle
              </TableCell>

              <TableCell>
                Location
              </TableCell>

              <TableCell>
                Activity
              </TableCell>

              <TableCell>
                Joined
              </TableCell>

              <TableCell>
                Status
              </TableCell>

              <TableCell>
                Verification
              </TableCell>

              <TableCell
                align="right"
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {visibleItems.map(
              (row) => {
                const fullyVerified =
                  isFullyVerified(row);

                return (
                  <TableRow
                    key={
                      row.courier_id
                    }
                    hover
                  >
                    <TableCell
                      sx={{
                        minWidth: 170,
                        position: "sticky",
                        left: 0,
                        zIndex: 2,
                        backgroundColor: "#fff",
                        borderRight: "1px solid #e5e7eb",
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                        }}
                      >
                        {displayName(
                          row
                        )}
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{
                          display:
                            "block",
                          color:
                            "text.secondary",
                        }}
                      >
                        Driver #
                        {
                          row.courier_id
                        }{" "}
                        · User #
                        {row.user_id}
                      </Typography>

                      {row.courier_name &&
                        row.courier_name !==
                          displayName(
                            row
                          ) && (
                          <Typography
                            variant="caption"
                            sx={{
                              display:
                                "block",
                              color:
                                "text.secondary",
                            }}
                          >
                            Profile:{" "}
                            {
                              row.courier_name
                            }
                          </Typography>
                        )}
                    </TableCell>

                    <TableCell
                      sx={{
                        minWidth: 220,
                      }}
                    >
                      <Typography
                        variant="body2"
                      >
                        {row.email ||
                          "-"}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color:
                            "text.secondary",
                        }}
                      >
                        {row.phone ||
                          "No phone"}
                      </Typography>

                      {row.username &&
                        row.username !==
                          row.email && (
                          <Typography
                            variant="caption"
                            sx={{
                              display:
                                "block",
                              color:
                                "text.secondary",
                            }}
                          >
                            Username:{" "}
                            {
                              row.username
                            }
                          </Typography>
                        )}
                    </TableCell>

                    <TableCell
                      sx={{
                        minWidth: 210,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          textTransform:
                            "capitalize",
                          fontWeight: 600,
                        }}
                      >
                        {row.courier_type ||
                          "Not specified"}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color:
                            "text.secondary",
                        }}
                      >
                        {displayVehicle(
                          row
                        )}
                      </Typography>
                    </TableCell>

                    <TableCell
                      sx={{
                        minWidth: 150,
                      }}
                    >
                      {displayLocation(
                        row
                      )}
                    </TableCell>

                    <TableCell
                      sx={{
                        minWidth: 170,
                      }}
                    >
                      <Typography
                        variant="body2"
                      >
                        {row.orders_count ??
                          0}{" "}
                        orders
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{
                          display:
                            "block",
                          color:
                            "text.secondary",
                        }}
                      >
                        Last:{" "}
                        {row.last_order_at
                          ? new Date(
                              row.last_order_at
                            ).toLocaleString()
                          : "No orders"}
                      </Typography>
                    </TableCell>

                    <TableCell
                      sx={{
                        minWidth: 150,
                      }}
                    >
                      {row.created_at
                        ? new Date(
                            row.created_at
                          ).toLocaleString()
                        : "-"}
                    </TableCell>

                    <TableCell
                      sx={{
                        minWidth: 170,
                      }}
                    >
                      {row.suspended ? (
                        <>
                          <Chip
                            label="SUSPENDED"
                            size="small"
                          />

                          {row.suspended_reason && (
                            <Typography
                              variant="caption"
                              sx={{
                                display:
                                  "block",
                                mt: 0.5,
                                maxWidth:
                                  200,
                                color:
                                  "text.secondary",
                              }}
                            >
                              {
                                row.suspended_reason
                              }
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Chip
                          label="ACTIVE"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </TableCell>

                    <TableCell
                      sx={{
                        minWidth: 220,
                      }}
                    >
                      <Box
                        sx={{
                          display:
                            "flex",
                          flexDirection:
                            "column",
                          alignItems:
                            "flex-start",
                        }}
                      >
                        <Chip
                          label={
                            fullyVerified
                              ? "FULLY VERIFIED"
                              : "NEEDS REVIEW"
                          }
                          size="small"
                          variant={
                            fullyVerified
                              ? "filled"
                              : "outlined"
                          }
                          sx={{
                            mb: 0.5,
                          }}
                        />

                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={
                                !!row.license_verified
                              }
                              onChange={(
                                e
                              ) =>
                                setFlag(
                                  row.courier_id,
                                  "license_verified",
                                  e.target
                                    .checked
                                )
                              }
                            />
                          }
                          label="License"
                        />

                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={
                                !!row.insurance_verified
                              }
                              onChange={(
                                e
                              ) =>
                                setFlag(
                                  row.courier_id,
                                  "insurance_verified",
                                  e.target
                                    .checked
                                )
                              }
                            />
                          }
                          label="Insurance"
                        />

                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={
                                !!row.vehicle_verified
                              }
                              onChange={(
                                e
                              ) =>
                                setFlag(
                                  row.courier_id,
                                  "vehicle_verified",
                                  e.target
                                    .checked
                                )
                              }
                            />
                          }
                          label="Vehicle"
                        />

                        <Typography
                          variant="caption"
                          sx={{
                            color:
                              "text.secondary",
                          }}
                        >
                          Verified at:{" "}
                          {row.verified_at
                            ? new Date(
                                row.verified_at
                              ).toLocaleString()
                            : "-"}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{
                        minWidth: 190,
                        whiteSpace:
                          "nowrap",
                        verticalAlign:
                          "top",
                      }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          saveVerification(
                            row.courier_id
                          )
                        }
                        disabled={
                          savingCourierId ===
                          row.courier_id
                        }
                        sx={{
                          mr: 1,
                          mb: 1,
                        }}
                      >
                        {savingCourierId ===
                        row.courier_id
                          ? "Saving…"
                          : "Save"}
                      </Button>

                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          openSuspendDialog(
                            row
                          )
                        }
                        sx={{
                          mb: 1,
                        }}
                      >
                        {row.suspended
                          ? "Reactivate"
                          : "Suspend"}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              }
            )}

            {!visibleItems.length &&
              !loading && (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    sx={{
                      color:
                        "text.secondary",
                    }}
                  >
                    No drivers or
                    carriers match the
                    current
                    search/filter.
                  </TableCell>
                </TableRow>
              )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={open}
                onClose={closeDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {target?.suspended
            ? "Reactivate Driver / Carrier"
            : "Suspend Driver / Carrier"}
        </DialogTitle>

        <DialogContent sx={{ mt: 1 }}>
          <Typography
            sx={{
              mb: 0.5,
              fontWeight: 600,
            }}
          >
            {target
              ? displayName(target)
              : "User"}
          </Typography>

          <Typography
            sx={{
              mb: 2,
              color: "text.secondary",
            }}
          >
            {target?.email || ""}
          </Typography>

          {!target?.suspended && (
            <TextField
              label="Suspension Reason (required)"
              value={reason}
              onChange={(e) =>
                setReason(e.target.value)
              }
              fullWidth
              multiline
              minRows={2}
            />
          )}

          {target?.suspended && (
            <Typography
              sx={{
                color: "text.secondary",
              }}
            >
              This will reactivate the account.
              Previous reason:{" "}
              {target?.suspended_reason || "none"}
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={closeDialog}>
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={confirmSuspendToggle}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}